/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-07-23
 * Updated: 2024-06-19
 */

// Add third-party dependencies.
import PropTypes from 'prop-types';
import { Alert, AlertTitle, Box, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Extract error message from various error structures.
 * @param {Object|String} error - The error object or string.
 * @param {Boolean} isLoginPage - Whether the component is rendered on the login page.
 * @param {Boolean} isRedirected - Whether the user was redirected from another page.
 * @returns {String} - Extracted error message.
 */
const getErrorMessage = (error, isLoginPage = false, isRedirected = false) => {
  if (!error) return null;

  // Handle HTTP error response.
  if (error.status) {
    // Only show session expired message if user was redirected or not on login page
    if (error?.data?.code === 'token_not_valid') {
      if (!isLoginPage || isRedirected) {
        return 'Your session has expired. Please log in again to continue.';
      }
      // Return null to prevent showing the error on login page when not redirected
      return null;
    }

    switch (error.status) {
      case 400:
        // For authentication errors, use a generic message
        if (error.data?.detail && error.data.detail.includes('credentials')) {
          return "We couldn't log you in with the provided credentials. Please check both your username and password and try again.";
        }
        return (
          error.data?.detail || 'There was an issue with your request. Please check your information and try again.'
        );
      case 401:
        // For login errors, show a more appropriate message
        if (isLoginPage && !isRedirected) {
          return 'Invalid username or password. Please check your credentials and try again.';
        }
        return 'Your session has expired or you are not logged in. Please log in to continue.';
      case 403:
        return "You don't have permission to access this resource. Please contact an administrator if you believe this is an error.";
      case 404:
        return 'The requested resource could not be found. Please check the URL and try again.';
      case 500:
        return "We're experiencing technical difficulties. Please try again later or contact support if the problem persists.";
      default:
        return error.message || error.statusText || error.status || error.data.messages[0].message;
    }
  }

  // Handle standard Error object.
  if (error instanceof Error) {
    return error.message;
  }

  // Handle error object with nested messages.
  if (error.message) {
    return error.message;
  }

  // Handle HTTP error response.
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }

  // Handle string errors.
  if (typeof error === 'string') {
    return error;
  }

  // Handle unknown error structures.
  return JSON.stringify(error);
};

/**
 * ErrorAlert component.
 * @example
 * return (
 *   <ErrorAlert
 *     error={new Error('This is an error message')}
 *     recoveryLinks={[
 *       { to: '/reset-password', text: 'Reset Password' },
 *       { to: '/help', text: 'Get Help' }
 *     ]}
 *   />
 * )
 * @param {Object|String} error - The error object or string.
 * @param {String} fallback - The fallback error message.
 * @param {String} severity - The severity level of the alert.
 * @param {Array} recoveryLinks - Optional array of recovery links.
 * @param {Boolean} isLoginPage - Whether the component is rendered on the login page.
 * @param {Boolean} isRedirected - Whether the user was redirected from another page.
 * @returns {React.ReactElement} component.
 */
const ErrorAlert = ({ error, fallback, severity, recoveryLinks, isLoginPage, isRedirected }) => {
  // Log error to console.
  if (severity === 'error') console.error('ErrorAlert:', error);

  // Set error message.
  const message = getErrorMessage(error, isLoginPage, isRedirected) || fallback;

  // Render component.
  return (
    <Alert
      severity={severity}
      sx={{
        mb: 2,
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <AlertTitle>{severity === 'error' ? 'Error' : 'Warning'}</AlertTitle>
      <Typography variant="body2">{message}</Typography>

      {recoveryLinks && recoveryLinks.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {recoveryLinks.map((link) => (
            <MuiLink
              key={`recovery-link-${link.to}`}
              component={RouterLink}
              to={link.to}
              color="inherit"
              sx={{ textDecoration: 'underline' }}
            >
              {link.text}
            </MuiLink>
          ))}
        </Box>
      )}
    </Alert>
  );
};

// Set component property types.
ErrorAlert.propTypes = {
  error: PropTypes.oneOfType([
    PropTypes.instanceOf(Error),
    PropTypes.shape({
      message: PropTypes.string,
      response: PropTypes.shape({
        data: PropTypes.shape({
          message: PropTypes.string,
        }),
      }),
    }),
    PropTypes.string,
    PropTypes.object,
  ]),
  fallback: PropTypes.string,
  severity: PropTypes.string,
  recoveryLinks: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
  isLoginPage: PropTypes.bool,
  isRedirected: PropTypes.bool,
};

// Set component default properties.
ErrorAlert.defaultProps = {
  error: null,
  fallback: 'An error occurred.',
  severity: 'error',
  recoveryLinks: null,
  isLoginPage: false,
  isRedirected: false,
};

// Export component.
export default ErrorAlert;
